import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import { OtherExpense } from "../types";
import { otherExpenseApi } from "../apiService";
import { formatCurrency, formatDate } from "../../../utils/utils";
import { useToast } from "../../../components/Toast";

interface ExpenseDetailViewProps {
  open: boolean;
  onClose: () => void;
  expenseId: string;
  onEdit?: (expense: OtherExpense) => void;
}

const ExpenseDetailView: React.FC<ExpenseDetailViewProps> = ({
  open,
  onClose,
  expenseId,
  onEdit,
}) => {
  const [expense, setExpense] = useState<OtherExpense | null>(null);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (open && expenseId) {
      fetchExpenseDetails();
    }
  }, [open, expenseId]);

  const fetchExpenseDetails = async () => {
    try {
      setLoading(true);
      const data = await otherExpenseApi.getExpenseById(expenseId);
      setExpense(data);
    } catch (error) {
      console.error("Error fetching expense details:", error);
      showToast("Failed to fetch expense details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (expense && onEdit) {
      onEdit(expense);
      onClose();
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      "Insurance": "primary",
      "Electricity Bill": "warning",
      "Water Bill": "info",
      "Internet/Phone Bill": "secondary",
      "Branch Rent": "error",
      "Branch Supplies": "success",
      "Legal & Professional": "default",
      "Marketing & Advertising": "primary",
      "Travel & Accommodation": "secondary",
      "Miscellaneous": "default",
    };
    return colors[category] || "default";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6">Expense Details</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : expense ? (
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Basic Information
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Expense Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(expense.expenseDate)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Category
              </Typography>
              <Box mt={0.5}>
                <Chip
                  label={expense.category}
                  color={getCategoryColor(expense.category) as any}
                  size="small"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Paid To
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {expense.paidTo}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {formatCurrency(expense.amount)}
              </Typography>
            </Grid>

            {/* Contact Information */}
            {(expense.contactNumber || expense.location) && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                    Contact Information
                  </Typography>
                  <Divider />
                </Grid>

                {expense.contactNumber && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Contact Number
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {expense.contactNumber}
                    </Typography>
                  </Grid>
                )}

                {expense.location && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {expense.location}
                    </Typography>
                  </Grid>
                )}
              </>
            )}

            {/* Notes */}
            {expense.notes && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                    Notes
                  </Typography>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1">
                    {expense.notes}
                  </Typography>
                </Grid>
              </>
            )}

            {/* Metadata */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Record Information
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1">
                {formatDate(expense.createdAt)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body1">
                {formatDate(expense.updatedAt)}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography>No expense data available</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {expense && onEdit && (
          <Button
            onClick={handleEdit}
            variant="contained"
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseDetailView; 